import React, { ElementType, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, ViewIcon } from '@chakra-ui/icons';
import { ArrowUpRight, CalendarDays, Package2, Plus, Truck, User2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import ConfirmModal from '../../components/ConfirmationModal';
import { deleteOrder, getOrders } from '../../hooks/orders';
import { getCompanyName, Order } from '../../utils/types';
import {
  OrderCommandBar,
  OrderDashboardSkeleton,
  OrderEmptyState,
  OrderFulfillmentBadge,
  OrderHero,
  OrderMetricStrip,
  OrderPaymentBadge,
  OrderPrimaryButton,
  OrderShell,
  OrderStatusBadge,
  formatOrderDate,
  formatOrderMoney,
  orderBorder,
  orderSurface,
} from './components/orderUi';

const normalizeOrders = (data: unknown): Order[] => {
  if (Array.isArray(data)) {
    return data as Order[];
  }

  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
    return (data as { results: Order[] }).results;
  }

  return [];
};

const getOrderCustomer = (order: Order) =>
  `${order.lead?.title ? `${order.lead.title} ` : ''}${order.lead?.first_name || ''} ${order.lead?.second_name || ''}`.trim() ||
  'Guest customer';

const getOrderCompany = (order: Order) =>
  getCompanyName(order.lead?.company) || order.customer?.company_name || 'No company';

const getOrderSearchIndex = (order: Order) =>
  [
    order.reference,
    getOrderCustomer(order),
    getOrderCompany(order),
    order.lead?.email,
    order.lead?.phone,
    order.source,
    order.total_amount,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

const sortOrders = (orders: Order[], sort: string) => {
  const next = [...orders];

  switch (sort) {
    case 'amount-asc':
      return next.sort((a, b) => Number(a.total_amount || 0) - Number(b.total_amount || 0));
    case 'amount-desc':
      return next.sort((a, b) => Number(b.total_amount || 0) - Number(a.total_amount || 0));
    case 'created-asc':
      return next.sort(
        (a, b) => new Date(a.created_at || '').getTime() - new Date(b.created_at || '').getTime(),
      );
    case 'created-desc':
    default:
      return next.sort(
        (a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime(),
      );
  }
};

type OrderItemProps = {
  order: Order;
  onOpen: (orderId: string) => void;
  onEdit: (orderId: string) => void;
  onDelete: (order: Order) => void;
};

const OrderTableRow = ({ order, onOpen, onEdit, onDelete }: OrderItemProps) => (
  <Tr _hover={{ bg: 'gray.50' }} transition="background 0.16s ease">
    <Td py={4}>
      <VStack align="flex-start" spacing={1}>
        <Text fontWeight="800" color="gray.900" cursor="pointer" onClick={() => onOpen(order.id)}>
          {order.reference || 'Reference pending'}
        </Text>
        <Text fontSize="sm" color="gray.500">
          {getOrderCustomer(order)}
        </Text>
      </VStack>
    </Td>
    <Td py={4}>
      <VStack align="flex-start" spacing={1}>
        <Text color="gray.900" fontWeight="600">
          {getOrderCompany(order)}
        </Text>
        <Text fontSize="sm" color="gray.500">
          {order.lead?.email || order.lead?.phone || 'No direct contact'}
        </Text>
      </VStack>
    </Td>
    <Td py={4}>
      <VStack align="flex-start" spacing={2}>
        <OrderStatusBadge status={order.status} />
        <OrderPaymentBadge status={order.payment_status} />
      </VStack>
    </Td>
    <Td py={4}>
      <OrderFulfillmentBadge status={order.fulfillment_status} />
    </Td>
    <Td py={4}>
      <VStack align="flex-start" spacing={1}>
        <Text color="gray.900" fontWeight="700">
          {formatOrderMoney(order.total_amount)}
        </Text>
        <Text fontSize="sm" color="gray.500">
          {order.items?.length || 0} item{(order.items?.length || 0) === 1 ? '' : 's'}
        </Text>
      </VStack>
    </Td>
    <Td py={4}>
      <VStack align="flex-start" spacing={1}>
        <Text color="gray.700">{formatOrderDate(order.created_at)}</Text>
        <Text fontSize="sm" color="gray.500">
          {order.updated_at ? `Updated ${formatOrderDate(order.updated_at)}` : 'Recently created'}
        </Text>
      </VStack>
    </Td>
    <Td py={4}>
      <HStack justify="flex-end" spacing={1}>
        <Tooltip label="Open order" hasArrow>
          <IconButton aria-label="Open order" icon={<ViewIcon />} variant="ghost" onClick={() => onOpen(order.id)} />
        </Tooltip>
        <Tooltip label="Edit order" hasArrow>
          <IconButton aria-label="Edit order" icon={<EditIcon />} variant="ghost" onClick={() => onEdit(order.id)} />
        </Tooltip>
        <Tooltip label="Delete order" hasArrow>
          <IconButton
            aria-label="Delete order"
            icon={<DeleteIcon />}
            colorScheme="red"
            variant="ghost"
            onClick={() => onDelete(order)}
          />
        </Tooltip>
      </HStack>
    </Td>
  </Tr>
);

const OrderCard = ({ order, onOpen, onEdit, onDelete }: OrderItemProps) => (
  <Box
    bg={orderSurface}
    borderWidth="1px"
    borderColor={orderBorder}
    borderRadius="24px"
    overflow="hidden"
    boxShadow="0 14px 36px rgba(15, 23, 42, 0.05)"
  >
    <Stack spacing={4} p={4}>
      <HStack justify="space-between" align="flex-start">
        <VStack align="flex-start" spacing={1}>
          <Text fontWeight="800" fontSize="lg" color="gray.900">
            {order.reference || 'Reference pending'}
          </Text>
          <Text fontSize="sm" color="gray.500">
            {getOrderCustomer(order)}
          </Text>
        </VStack>
        <OrderStatusBadge status={order.status} />
      </HStack>

      <Stack spacing={3}>
        <HStack spacing={2} color="gray.600">
          <Box as={User2 as ElementType} boxSize={4} />
          <Text fontSize="sm">{getOrderCompany(order)}</Text>
        </HStack>
        <HStack spacing={2} color="gray.600">
          <Box as={CalendarDays as ElementType} boxSize={4} />
          <Text fontSize="sm">{formatOrderDate(order.created_at)}</Text>
        </HStack>
        <HStack spacing={2} color="gray.600">
          <Box as={Package2 as ElementType} boxSize={4} />
          <Text fontSize="sm">
            {order.items?.length || 0} item{(order.items?.length || 0) === 1 ? '' : 's'}
          </Text>
        </HStack>
        <HStack spacing={2} color="gray.600">
          <Box as={Truck as ElementType} boxSize={4} />
          <Text fontSize="sm">{order.fulfillment_status?.replace(/_/g, ' ') || 'Not started'}</Text>
        </HStack>
      </Stack>

      <HStack justify="space-between" align="flex-end">
        <VStack align="flex-start" spacing={1}>
          <OrderPaymentBadge status={order.payment_status} />
          <Text fontWeight="800" fontSize="xl" color="gray.900">
            {formatOrderMoney(order.total_amount)}
          </Text>
        </VStack>
        <HStack spacing={2}>
          <Button
            borderRadius="16px"
            rightIcon={<Box as={ArrowUpRight as ElementType} />}
            onClick={() => onOpen(order.id)}
          >
            Open
          </Button>
          <IconButton
            aria-label="Edit order"
            icon={<EditIcon />}
            borderRadius="16px"
            variant="ghost"
            onClick={() => onEdit(order.id)}
          />
          <IconButton
            aria-label="Delete order"
            icon={<DeleteIcon />}
            borderRadius="16px"
            variant="ghost"
            colorScheme="red"
            onClick={() => onDelete(order)}
          />
        </HStack>
      </HStack>
    </Stack>
  </Box>
);

export default function OrdersListPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [source, setSource] = useState('all');
  const [sort, setSort] = useState('created-desc');
  const [view, setView] = useState<'table' | 'cards'>('table');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === '/' && document.activeElement !== searchInputRef.current) {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const fetchOrders = async () => {
    if (!orgId) {
      return;
    }

    setLoading(true);
    const response = await getOrders(orgId);
    setLoading(false);

    if (!response.success) {
      toast({
        title: 'Could not load orders',
        description: 'Please refresh and try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    setOrders(normalizeOrders(response.data));
  };

  useEffect(() => {
    fetchOrders();
  }, [orgId]);

  const filteredOrders = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = orders.filter((order) => {
      const matchesSearch = !query || getOrderSearchIndex(order).includes(query);
      const matchesStatus = status === 'all' || order.status === status;
      const matchesSource = source === 'all' || order.source === source;
      return matchesSearch && matchesStatus && matchesSource;
    });

    return sortOrders(filtered, sort);
  }, [orders, search, source, sort, status]);

  const stats = useMemo(
    () => [
      { label: 'Orders', value: String(orders.length) },
      { label: 'Pending', value: String(orders.filter((order) => order.status === 'pending').length) },
      { label: 'Processing', value: String(orders.filter((order) => order.status === 'processing').length) },
      {
        label: 'Order value',
        value: formatOrderMoney(
          orders.reduce((sum, order) => sum + Number(order.total_amount || 0), 0),
        ),
      },
    ],
    [orders],
  );

  const handleOpen = (orderId: string) => {
    navigate(`/organization/${orgId}/orders/${orderId}`);
  };

  const handleEdit = (orderId: string) => {
    navigate(`/organization/${orgId}/orders/${orderId}/edit`);
  };

  const handleDeleteClick = (order: Order) => {
    setSelectedOrder(order);
    onOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!orgId || !selectedOrder?.id) {
      return;
    }

    setDeleting(true);
    const response = await deleteOrder(orgId, selectedOrder.id);
    setDeleting(false);

    if (!response.success) {
      toast({
        title: 'Could not delete order',
        description: 'Please try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    setOrders((current) => current.filter((order) => order.id !== selectedOrder.id));
    onClose();
    toast({
      title: 'Order deleted',
      description: `${selectedOrder.reference || 'Order'} was removed.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <OrderShell>
      <OrderHero
        title="Orders that feel clear, fast, and ready for real operations"
        description="Track every order from first request to completion with live totals, clearer delivery progress, and a calmer workflow for day-to-day operations."
        stats={stats}
        primaryAction={
          <OrderPrimaryButton leftIcon={<Plus size={16} />} onClick={() => navigate(`/organization/${orgId}/orders/new`)}>
            New order
          </OrderPrimaryButton>
        }
        secondaryAction={
          <Button borderRadius="16px" variant="outline" onClick={fetchOrders}>
            Refresh
          </Button>
        }
      />

      <OrderMetricStrip items={stats} />

      <OrderCommandBar
        search={search}
        onSearchChange={setSearch}
        inputRef={searchInputRef}
        status={status}
        onStatusChange={setStatus}
        source={source}
        onSourceChange={setSource}
        sort={sort}
        onSortChange={setSort}
        view={view}
        onViewChange={setView}
        resultLabel={`${filteredOrders.length} order${filteredOrders.length === 1 ? '' : 's'} shown`}
      />

      {loading ? (
        <OrderDashboardSkeleton />
      ) : filteredOrders.length === 0 ? (
        <OrderEmptyState
          title={orders.length === 0 ? 'No orders yet' : 'No orders match these filters'}
          description={
            orders.length === 0
              ? 'Create your first order to start tracking customers, items, delivery progress, and payment status in one place.'
              : 'Try changing the search, source, or status filters to bring matching orders back into view.'
          }
          action={
            orders.length === 0 ? (
              <OrderPrimaryButton leftIcon={<Plus size={16} />} onClick={() => navigate(`/organization/${orgId}/orders/new`)}>
                Create your first order
              </OrderPrimaryButton>
            ) : undefined
          }
        />
      ) : view === 'cards' ? (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
          {filteredOrders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              onOpen={handleOpen}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
            />
          ))}
        </SimpleGrid>
      ) : (
        <Box
          bg="white"
          borderWidth="1px"
          borderColor={orderBorder}
          borderRadius="24px"
          boxShadow="0 14px 36px rgba(15, 23, 42, 0.05)"
          overflow="hidden"
        >
          <TableContainer>
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Order</Th>
                  <Th>Company</Th>
                  <Th>Status</Th>
                  <Th>Fulfillment</Th>
                  <Th>Amount</Th>
                  <Th>Timeline</Th>
                  <Th textAlign="right">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredOrders.map((order) => (
                  <OrderTableRow
                    key={order.id}
                    order={order}
                    onOpen={handleOpen}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                  />
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        handleConfirm={handleDeleteConfirm}
        isLoading={deleting}
        AreYouSureQuestion={`Delete ${selectedOrder?.reference || 'this order'}?`}
        AreYouSureTextExplanation="This order and its line items will be permanently removed."
        confirmButtonText="Delete order"
        confirmButtonColor="#E53E3E"
        colorScheme="red"
      />
    </OrderShell>
  );
}
